import axios from 'axios';
import isObject from 'lodash/isObject';

import { getDefaultConfiguration } from '@app/helpers/configurationHelpers';
import { updateToken, updateDeviceToken, resetUser } from '@app/actions/userActions';
import { store } from '@app';

/**
 * @param {Object} options
 * @param {String} options.method
 * @param {String} options.path
 * @param {Object} [options.data] - the data to be sent as the request body
 * @param {Object} [options.params] - the URL parameters to be sent with the request
 * @param {Object} [options.headers]
 * @return {Promise}
 */
export default function sendRequest({
  method,
  path,
  data = {},
  params,
  headers,
}) {
  const { webserviceKey, apiUrl } = getDefaultConfiguration();
  const href = apiUrl + path;

  const defaultHeaders = {
    'content-type': 'multipart/form-data',
    'x-an-webservice-identitykey': webserviceKey,
  };

  const bodyFormData = new FormData();

  Object.entries(data).forEach(([key, value]) => {
    bodyFormData.append(key, isObject(value) ? JSON.stringify(value) : value);
  });

  const config = {
    async: true,
    crossDomain: true,
    url: href,
    method,
    headers: { ...defaultHeaders, ...headers },
    data: bodyFormData,
    params,
    paramsSerializer: (params = {}) => {
      const str = Object.entries(params).map(([key, value]) => (
        `${encodeURIComponent(key)}=${encodeURIComponent(JSON.stringify(value))}`
      ));

      return str.join('&');
    },
  };

  return sendRequestJson({ config, headers })
    .catch((e) => {
      console.error('Error on ', path, ': ', e);

      return Promise.resolve({
        error: {
          code: 404,
        },
      });
    });
}


function sendRequestJson({ config, headers }) {
  return axios({
    ...config,
    /**
     * The problem: a long numbers are truncated from "5653814518315977731" to "5653814518315978000"
     *
     * In JavaScript, all numbers are stored as floating-point, which is like scientific notation in base 2 instead
     * of base 10. There is a fixed number of bits (53 in this case) available to represent a in a * (2 ** b).
     * If we look at Number.MAX_SAFE_INTEGER, the greatest integer that can be represented with 53 bits of precision,
     * we will see that it has 16 base-10 digits. Our number has 19. So to store such large numbers, it is usually
     * best to store them as strings.
     * */
    transformResponse: [
      data => data.replace(/("[^"]*"\s*:\s*)(\d{16,})/g, '$1"$2"'),
      ...axios.defaults.transformResponse,
    ],
  })
    .then((response) => {
      const { result, error } = response.data;

      if (result && result.newAuthToken) {
        if (result === 'undefined' || result === 'null') {
          store.dispatch(resetUser());
        } else {
          store.dispatch(updateToken(result.newAuthToken));
        }

        delete response.data.result.newAuthToken;
      }

      if (result && result.deviceAuthToken) {
        store.dispatch(updateDeviceToken(result.deviceAuthToken));
        delete response.data.result.deviceAuthToken;
      }

      if (headers && headers['x-an-webservice-customerauthtoken'] && error && (error.code === -1 || error.code === -9)) {
        store.dispatch(resetUser());
      }

      return response.data;
    });
}
